import type { ReactNode } from "react";

import type {
  DryRunPanelReport,
  DryRunReport,
} from "@/shared/domain/boards/boardAuthoringService";
import {
  ConfirmationAlert,
  ConfirmationBullets,
  ConfirmationField,
  ConfirmationNote,
} from "@/shared/foundation/primitives";

import {
  describePanelReport,
  describeStageReport,
  failureReasons,
  listPhrase,
  warningPhrases,
} from "./boardConfirmationText";
import type { BoardDryRunState } from "./useBoardDryRun";

/**
 * The pieces of dry-run status the create and update confirmation cards
 * share: the overall verdict above the card, the per-panel and per-stage
 * lines inside it, the list of what failed, and the note on what approving
 * does. Both cards render their proposal first and let these fill in. A dry
 * run that never started (`not-run`) adds nothing to the card.
 */

/** The dry run's verdict, once it has one. */
export function DryRunVerdict({
  dryRun,
  shownOnCard = [],
}: {
  dryRun: BoardDryRunState;
  /** Reasons a field of the card already shows (the time zone's), which
   *  the verdict does not repeat. */
  shownOnCard?: readonly (string | undefined)[];
}) {
  if (dryRun.status === "failed") {
    return (
      <ConfirmationNote>
        The board could not be checked in this browser. Approving checks it
        again.
      </ConfirmationNote>
    );
  }
  if (dryRun.status !== "ready") {
    return null;
  }
  if (dryRun.report.status === "blocked") {
    return (
      <>
        <ConfirmationAlert>
          {describeBlockedCheck(dryRun.report)}
        </ConfirmationAlert>
        <FailureReasons
          reasons={(dryRun.report.definition_problem_reasons ?? []).filter(
            (reason) => !shownOnCard.includes(reason),
          )}
        />
        <ViewerChoicesNote report={dryRun.report} />
      </>
    );
  }
  return (
    <>
      <ReportWarnings warnings={dryRun.report.warnings} />
      <ViewerChoicesNote report={dryRun.report} />
    </>
  );
}

/**
 * How the check used what the person chose in the board's controls: the
 * controls it ran with their choice, and each control whose choice will not
 * fit it after the change, with what it falls back to. Nothing renders when
 * the board is not on screen or nothing was chosen.
 */
function ViewerChoicesNote({ report }: { report: DryRunReport }) {
  const { used = [], unfit = [] } = report.viewer_choices ?? {};
  const sentences = unfit.map(
    ({ control, default_used }) =>
      `"${control}" will not accept your current choice after this change, so ${default_used ? "it will go back to its default" : "you will need to choose again"}.`,
  );
  if (used.length > 0) {
    const choices = used.length === 1 ? "choice" : "choices";
    const controls = listPhrase(used.map((caption) => `"${caption}"`));
    sentences.unshift(`The check used your current ${choices} in ${controls}.`);
  }
  if (sentences.length === 0) {
    return null;
  }
  return <ConfirmationNote>{sentences.join(" ")}</ConfirmationNote>;
}

/**
 * What the dry run noted about the board rather than about one panel, such
 * as a section with a selection of its own that no panel inside it sets,
 * each kind of note once (`warningPhrases`). Nothing renders when it noted
 * nothing; a report whose status is `blocked` shows the failure alert
 * instead.
 */
function ReportWarnings({ warnings }: { warnings: readonly string[] }) {
  if (warnings.length === 0) {
    return null;
  }
  const phrases = warningPhrases(warnings);
  const count = phrases.length === 1 ? "one thing" : `${phrases.length} things`;
  return (
    <ConfirmationNote>
      The check noted {count} to look at: {phrases.join("; ")}.
    </ConfirmationNote>
  );
}

/** What each top-level member of a definition is called on the card, and
 *  whether those words take a plural verb. */
const DEFINITION_MEMBER_WORDS: Readonly<
  Record<string, { words: string; plural: boolean }>
> = {
  schema_version: { words: "the board's format version", plural: false },
  title: { words: "the title", plural: false },
  time_range: { words: "the time range", plural: false },
  calendar: { words: "the time zone and week start", plural: true },
  stages: { words: "the stages", plural: true },
  panels: { words: "the panels", plural: true },
  sections: { words: "the sections", plural: true },
  selections: { words: "the selections", plural: true },
};

const WHOLE_DEFINITION = { words: "the board's JSON", plural: false };

/**
 * The verdict of a check that failed: what failed, then that approving
 * changes nothing and the chat is told why. A definition the check could not
 * read names the parts of it that are not valid; any other names how many
 * stages and panels failed, which `CheckFailures` lists.
 */
function describeBlockedCheck(report: DryRunReport): string {
  const outcome = "Approving changes nothing and tells the chat what failed.";
  if (report.definition_problems !== undefined) {
    const members = (report.definition_problem_members ?? []).map(
      (member) => DEFINITION_MEMBER_WORDS[member] ?? WHOLE_DEFINITION,
    );
    const parts = members.length === 0 ? [WHOLE_DEFINITION] : members;
    const words = [...new Set(parts.map((part) => part.words))];
    const verb = words.length > 1 || parts[0].plural ? "are" : "is";
    return `The check failed: ${listPhrase(words)} ${verb} not valid. ${outcome}`;
  }
  const failures = [
    countOf(
      report.stages.filter((stage) => stage.status === "failed").length,
      "stage",
    ),
    countOf(
      report.panels.filter((panel) => panel.status === "failed").length,
      "panel",
    ),
  ].filter((count) => count !== undefined);
  return failures.length === 0
    ? `The check failed. ${outcome}`
    : `The check failed for ${listPhrase(failures)}. ${outcome}`;
}

/** `1 stage`, `3 panels`, or undefined for none. */
function countOf(count: number, noun: string): string | undefined {
  if (count === 0) {
    return undefined;
  }
  return count === 1 ? `1 ${noun}` : `${count} ${noun}s`;
}

/** One panel's dry-run status, appended to its line. */
export function PanelDryRunStatus({
  dryRun,
  panelId,
  panelIndex,
}: {
  dryRun: BoardDryRunState;
  panelId: string | undefined;
  /**
   * The panel's index in the `panels` list of the definition the dry run
   * ran on. When given, the report at that index is used rather than the one
   * found by id, since a panel with no id or a duplicated id cannot be found
   * by id.
   */
  panelIndex?: number;
}) {
  if (dryRun.status === "loading") {
    return <> (checking)</>;
  }
  if (dryRun.status !== "ready") {
    return null;
  }
  const report =
    panelIndex === undefined
      ? dryRun.report.panels.find((panel) => panel.id === panelId)
      : dryRun.report.panels[panelIndex];
  if (report === undefined) {
    return null;
  }
  return (
    <>
      {" "}
      ({describePanelReport(report, dryRun.report.stages)})
      <FailureReasons
        reasons={failureReasons(report.parse ?? report.failures[0])}
      />
    </>
  );
}

/** One stage's dry-run status, appended to its line. */
export function StageDryRunStatus({
  dryRun,
  stageId,
  stageIndex,
}: {
  dryRun: BoardDryRunState;
  stageId: string | undefined;
  /**
   * The stage's index in the `stages` list of the definition the dry run
   * ran on, matched against the report's `index` rather than the id, since a
   * stage with no id or a duplicated id cannot be found by id.
   */
  stageIndex?: number;
}) {
  if (dryRun.status === "loading") {
    return <> (checking)</>;
  }
  if (dryRun.status !== "ready") {
    return null;
  }
  const report = dryRun.report.stages.find((stage) =>
    stageIndex === undefined
      ? stage.stage === stageId
      : stage.index === stageIndex,
  );
  if (report === undefined) {
    return null;
  }
  return (
    <>
      {" "}
      ({describeStageReport(report)})
      <FailureReasons reasons={failureReasons(report.failure)} />
    </>
  );
}

/**
 * The note under a card that says what approving does. It shows only while
 * approving can save, which is:
 * - once the definition approving would save is known (`definitionKnown`):
 *   never for a request the tool refuses, and not while an update's changes
 *   are still being applied to a copy of the stored board;
 * - until the check fails, since the verdict then says that approving
 *   changes nothing.
 */
export function ApprovalNote({
  dryRun,
  definitionKnown,
  children,
}: {
  dryRun: BoardDryRunState;
  definitionKnown: boolean;
  children: ReactNode;
}) {
  if (
    !definitionKnown ||
    (dryRun.status === "ready" && dryRun.report.status === "blocked")
  ) {
    return null;
  }
  return <ConfirmationNote>{children}</ConfirmationNote>;
}

/**
 * Every stage, then every panel, that failed the check, each with what
 * failed and why: the same entries the verdict counts, so a person can see
 * each reason the board would not be saved, including a stage or panel that
 * no other line of the card names. `panelName` names a failed panel, given
 * its place in the report's `panels`.
 */
export function CheckFailures({
  dryRun,
  panelName,
}: {
  dryRun: BoardDryRunState;
  panelName: (panel: DryRunPanelReport, index: number) => string;
}) {
  if (dryRun.status !== "ready") {
    return null;
  }
  const { stages, panels } = dryRun.report;
  const lines = [
    ...stages.flatMap((stage, index) =>
      stage.status === "failed"
        ? [
            {
              key: `stage:${index}`,
              name:
                stage.stageName === stage.address
                  ? `Stage ${(stage.index ?? index) + 1} in the board's JSON`
                  : `The stage "${stage.stageName}"`,
              status: describeStageReport(stage),
              reasons: failureReasons(stage.failure),
            },
          ]
        : [],
    ),
    ...panels.flatMap((panel, index) =>
      panel.status === "failed"
        ? [
            {
              key: `panel:${index}`,
              name: panelName(panel, index),
              status: describePanelReport(panel, stages),
              reasons: failureReasons(panel.parse ?? panel.failures[0]),
            },
          ]
        : [],
    ),
  ];
  if (lines.length === 0) {
    return null;
  }
  return (
    <ConfirmationField label="What failed the check">
      <ConfirmationBullets>
        {lines.map((line) => (
          <li key={line.key}>
            {line.name}: {line.status}
            <FailureReasons reasons={line.reasons} />
          </li>
        ))}
      </ConfirmationBullets>
    </ConfirmationField>
  );
}

/** Why a stage or panel failed, under its line; nothing when its line says
 *  all a person is told. */
function FailureReasons({ reasons }: { reasons: readonly string[] }) {
  if (reasons.length === 0) {
    return null;
  }
  return <ConfirmationNote>{reasons.join(" ")}</ConfirmationNote>;
}
